import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import ReactGA from 'react-ga';
import Tour from 'reactour';
import { useIdleTimer } from 'react-idle-timer'

import MapView from './mapview/mapview';
import GDPRModal from './modals/gdpr-modal/gdpr-modal';

import { IGDPRPreferences } from '../types/helpertext-config';
import config from '../config/config';
import { RootState } from '../reducers/app';

import { timeout_initialLimit } from '../config/auth.config';

import './app.scss';
import AuthController from '../controllers/AuthController';
import InactivityModal from './modals/inactivity-modal/inactivity-modal';

function App() {
  const [modalVisible, setModalVisible] = useState(false);
  const [preferences, setPreferences] = useState<IGDPRPreferences | null>(null);
  const [tourPreferences, setTourPreferences] = useState<string>(
    localStorage.getItem('show-tour-preferences') || 'true'
  );
  const [showTour, setShowTour] = useState<boolean>(!!JSON.parse(tourPreferences));
  const [currentStep, setCurrentStep] = useState<number>(0);

  const [sessionEndingSoon, setSessionEndingSoon] = useState<boolean>(false);

  const mapReady = useSelector((state: RootState) => state.mapview.mapReady);
  
  const defaultPreferences = config.helpertext.gdprPreferences as IGDPRPreferences;

  const savePreferences = (acceptAll: boolean, updatedPreferences: IGDPRPreferences) => {
    setPreferences(acceptAll ? defaultPreferences : updatedPreferences);
    setModalVisible(false);
  };

  useEffect(() => {
    const gdprPreferences = localStorage.getItem('gdpr-preferences');
    // If we have preferences in local storage, check to see if we can run analytics
    if (gdprPreferences) {
      const preferencesFromLocalStorage: IGDPRPreferences = JSON.parse(gdprPreferences);
      if (preferencesFromLocalStorage?.performance.value) {
        //ReactGA.initialize('UA-310737-9');
        ReactGA.initialize('UA-50139914-20');
        ReactGA.pageview(window.location.pathname + window.location.search);
      }
    } else {
      // If we don't, then we check to see if we have local preferences and check to see if we can run analytics
      if (preferences) {
        localStorage.setItem('gdpr-preferences', JSON.stringify(preferences));
        if (preferences.performance.value) {
          //ReactGA.initialize('UA-310737-9');
          ReactGA.initialize('UA-50139914-20');
          ReactGA.pageview(window.location.pathname + window.location.search);
        }
        // If we have neither, show the modal
      } else {
        setModalVisible(true);
      }
    }
  }, [preferences]);

  const handleOnIdle = (event: Event) => {
    if (!sessionEndingSoon) {
      setSessionEndingSoon(true);
    }
  }

  const { reset } = useIdleTimer({
    timeout: timeout_initialLimit,
    onIdle: handleOnIdle,
    debounce: 500
  });

  const logUserOut = () => {
    resetTimerState();
    AuthController.logout();
  };

  const resetTimerState = () => {
    setSessionEndingSoon(false);
    setTimeout(timeout_initialLimit);
    reset();
  };

  const disableTour = (value: boolean) => {
    localStorage.setItem('show-tour-preferences', value.toString());
    setTourPreferences(value.toString());
  };

  const steps = [
    {
      // selector: '[data-tut="step-zero"]',
      content: () => (
        <div className='tour-content welcome'>
          <div className='tour-text header'>Welcome to the FSC GIS Portal</div>
          <div className='tour-text'>
            With this app, you will be able to detect, identify and locate disturbances and affectations of the forest.
          </div>
          <div className='tour-text'>
            The app provides access to spatial forest-relevant information such as tree cover loss, Intact Forest
            Landscapes (IFL), protected areas, Indigenous and Community lands, watercourses, and up-to-date satellite
            imagery from different sources, as well as the different boundaries voluntarily provided (displayed on FSC
            on the Map). The spatial analysis identifies, highlights, and geographically locates the affectations of the
            forest cover within the certified areas or areas of interest; and will be linked to the relevant FSC
            criterion (as per FSC-STD-01-001).
          </div>
          <div className='tour-text welcome-bottom'>
            The app is not developed to perform/replace the audit process nor to make final decisions for you, it's goal
            is to get insights on what is happening on the field and warn you about potential non-conformities. You can
            use the app to plan an upcoming audit, identify concerning areas before going on the field and verify audit
            evidence.
          </div>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
    {
      selector: '[data-tut="step-one"]',
      content: () => (
        <div className='tour-content'>
          <div className='tour-text'>
            View and interact with featured data layers selected by FSC. Layers and annotations created while using the
            tool will also appear here.
          </div>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
    {
      selector: '[data-tut="step-two"]',
      content: () => (
        <div className='tour-content'>
          <div>Select the Forest Management Unit to audit by:</div>
          <ul>
            <li>Searching and selecting a location from the FSC Boundaries Layer</li>
            <li>Uploading a zipped shapefile of the FMU</li>
            <li>Uploading a KML/KMZ of the FMU</li>
            <li>Draw a polygon</li>
          </ul>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
    {
      selector: '[data-tut="step-three"]',
      content: () => (
        <div className='tour-content'>
          <div className='tour-text'>
            <div>
              Select Landsat or Sentinel satellite images of the initial state and final state of your Audit Period.
              Then, select a vegetation index to use to create an image comparing the initial state with the final
              state. This vegetation index difference image will appear in My Layers. You can create additional
              difference images as needed.
            </div>
            <br />
            <div>
              Difference images can be used with the University of Maryland (UMD) Tree Cover Loss data to identify areas
              of deforestation during the inventory period. The two methods offer different approaches to identifying
              deforestation. Difference images calculated with this tool show the change in detected vegetation between
              the dates you select while the UMD data are calculated by analyzing imagery over an entire year to
              identify any pixels of forest loss during that time.
            </div>
          </div>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
    {
      selector: '[data-tut="step-four"]',
      content: () => (
        <div className='tour-content'>
          <div className='tour-text'>
            Draw or upload additional contextual data, and calculate riparian boundaries, that will be used to assess
            compliance with FSC’s principles and criteria. Data can be zipped shapefiles or kml files and will appear
            under My Layers. These layers can be combined or intersected with other layers for analysis.
          </div>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
    {
      selector: '[data-tut="step-five"]',
      content: () => (
        <div className='tour-content'>
          <div className='tour-text'>
            Merge or intersect featured layers, riparian boundaries, and other contextual data to create new areas of
            interest (AOI) for use in analysis.
          </div>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
    {
      selector: '[data-tut="step-six"]',
      content: () => (
        <div className='tour-content'>
          <div className='tour-text'>
            Select an AOI from Featured Layers or My Layers to use when assessing compliance with each principle and
            criteria. Tree cover loss for that AOI will be calculated using the University of Maryland’s Global Forest
            Change data. Change during the audit period within the AOI based on your comparison image(s) will also be
            highlighted. Use the toggles to switch between principles during your analysis.
          </div>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
    {
      selector: '[data-tut="step-seven"]',
      content: () => (
        <div className='tour-content'>
          <div className='tour-text'>Search the FSC Certified Forests Layer (not from the FSC Certificate database) by company name or certificate number. Locate places by name or address.</div>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
    {
      selector: '[data-tut="step-eight"]',
      content: () => (
        <div className='tour-content'>
          <div className='tour-text'>Measure distances and areas.</div>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
    {
      selector: '[data-tut="step-nine"]',
      content: () => (
        <div className='tour-content'>
          <div className='tour-text'>
            Use the annotation tool to take note of areas for further investigation or follow up. These will be added to
            the My Annotations section of the layer panel.
          </div>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
    {
      selector: '[data-tut="step-ten"]',
      content: () => (
        <div className='tour-content'>
          <div className='tour-text'>
            Print a report that includes the Area (Ha) within your AOIs using the University of Maryland's Tree Cover
            Loss data.
          </div>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
    {
      selector: '[data-tut="step-eleven"]',
      content: () => (
        <div className='tour-content'>
          <div className='tour-text'>Swipe to compare images of your initial state and final state.</div>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
    {
      selector: '[data-tut="step-twelve"]',
      content: () => (
        <div className='tour-content'>
          <div className='tour-text'>
            Click on a layer's point/polygon on the map to open a pop-up and see more information about that feature.
          </div>
          <div className='tour-skip'>
            <input
              id='tour-skip'
              type='checkbox'
              checked={!!JSON.parse(tourPreferences)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => disableTour(e.target.checked)}
            />
            <label htmlFor='tour-skip'>Show tour in the future</label>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className='container'>
      {sessionEndingSoon && <InactivityModal handleLogout={() => logUserOut()} handleResume={() => resetTimerState()} />}
      <MapView setShowTour={setShowTour} />
      {modalVisible && <GDPRModal savePreferences={savePreferences} preferences={defaultPreferences} />}
      {mapReady && !modalVisible && (
        <Tour
          steps={steps}
          isOpen={showTour}
          onRequestClose={() => setShowTour(false)}
          closeWithMask={false}
          accentColor='#285c4d'
          maskSpace={0}
          startAt={0}
          className={`${currentStep === 0 ? 'extra-width' : 'reg-width'}`}
          getCurrentStep={(step) => setCurrentStep(step)}
        />
      )}
    </div>
  );
}

export default App;
